import {HttpErrorResponse} from '@angular/common/http';
import {Injectable, NgZone} from '@angular/core';
import {
    BusinessErrorCode,
    CommonErrorHandler,
    ERROR_PARAM,
    ERROR_PATH,
    ERROR_REQUEST_ID_PARAM,
    RouterService,
    toBusinessOrNull,
} from '@npck/libs/commons';
import {DialogService, SnackbarService} from '@npck/libs/ui-kit';
import {AuthHolderService} from '../domain/auth-holder.service';

const snackbarErrors = [
    BusinessErrorCode.AF_ACCESS_DENIED,
    BusinessErrorCode.AF_FORBIDDEN,
    BusinessErrorCode.AF_ACCESS_DENIED_DELETE_INCIDENT,
    BusinessErrorCode.AF_FAILED_PASSWORD_UPDATE,
    BusinessErrorCode.AF_IDENTIFICATION_NUMBER_IMMUTABLE,
    BusinessErrorCode.AF_REPORT_EXPORT_ACCESSES_DENIED,
    BusinessErrorCode.AF_REPORT_IMPORT_ACCESSES_DENIED,
    BusinessErrorCode.AF_SUBJECT_ALREADY_EXISTS,
    BusinessErrorCode.AF_SUBJECT_ACCESS_DENIED,
    BusinessErrorCode.AF_USER_DISABLED,
    BusinessErrorCode.AF_USER_EMAIL_ALREADY_EXISTS,
    BusinessErrorCode.AF_USER_USERNAME_ALREADY_EXISTS,
    BusinessErrorCode.AF_USER_ALREADY_EXISTS,
    BusinessErrorCode.AF_THERE_IS_NO_RECORDS,
    BusinessErrorCode.AF_FILE_HAS_VIRUS,
    BusinessErrorCode.AF_FILE_WRONG_EXTENSION,
    BusinessErrorCode.AF_IMPORT_ERROR,
    BusinessErrorCode.AF_EXPORT_ERROR,
    BusinessErrorCode.AF_ORGANIZATION_BIN_ALREADY_EXISTS,
    BusinessErrorCode.AF_ORGANIZATION_DELETED,
    BusinessErrorCode.AF_ORGANIZATION_DELETED_NOT_ALLOWED,
    BusinessErrorCode.AF_ORGANIZATION_DELETE_DISABLED,
    BusinessErrorCode.AF_ORGANIZATION_DOCUMENT_TYPE_INVALID,
    BusinessErrorCode.AF_ORGANIZATION_DOCUMENT_DELETED,
    BusinessErrorCode.AF_REFERENCE_BOOK_BIN_READONLY,
    BusinessErrorCode.AF_PASSWORD_RECOVERY_RATE_LIMITED,
    BusinessErrorCode.AF_SUPERSET_DASHBOARD_DUPLICATE,
    BusinessErrorCode.AF_SUPERSET_DASHBOARD_INVALID_ROLE,
    BusinessErrorCode.AF_TRANSACTION_USED_IN_REFUND,
    BusinessErrorCode.AF_INCIDENT_MSISDN_FRAUD_ENTRY_REQUIRED,
    BusinessErrorCode.AF_REFUND_REJECTION_BIOMETRIC_FAILED,
    BusinessErrorCode.AF_REFUND_REJECTION_IIN_NOT_AVAILABLE,
    BusinessErrorCode.AF_REFUND_REJECTION_PHONE_NOT_AVAILABLE,
];

@Injectable()
export class AppErrorHandler extends CommonErrorHandler {

    constructor(
        zone: NgZone,
        routerService: RouterService,
        private authHolderService: AuthHolderService,
        private dialogService: DialogService,
        private snackbarService: SnackbarService,
    ) {
        super(zone, routerService);
    }

    hideAllUiElements(): void {
        super.hideAllUiElements();
        this.snackbarService.dismiss();
        this.dialogService.closeAllDialogs();
    }

    handleAccessError(_: HttpErrorResponse): void {
        this.authHolderService.logout('session_ended');
    }

    handleNetworkError(): void {
        this.routerService.navigate(ERROR_PATH, {queryParams: {[ERROR_PARAM]: Error.NETWORK_CONNECTIVITY_ERROR}});
    }

    handleError(error: any): void {
        this.zone.run(() => {
            this.hideAllUiElements();
            if (!(error instanceof HttpErrorResponse)) {
                super.handleError(error);
                return;
            }
            let errorType = Error.SERVER_ERROR;
            const queryParams: Record<string, any> = {};
            prepareHttpErrorForBusinessParsing(error).then(httpError => {
                const businessError = toBusinessOrNull(httpError);
                if (businessError && snackbarErrors.includes(businessError.error)) {
                    this.snackbarService.showSnackBar(
                        businessError.errorDescription ?? 'i18n_af_snackBarMessage#' + businessError.error,
                        'error',
                        false,
                    );
                    return;
                }
                if (error instanceof HttpErrorResponse) {
                    const responseBody = error.error;
                    const validationErrors: ValidationErrorItem[] = responseBody?.errors;
                    if (error.status === 400 && validationErrors?.length) {
                        const messages = [...new Set(
                            validationErrors.filter(item => item !== null).map(item => item.message)
                        )];
                        if (messages.length > 0) {
                            this.snackbarService.showSnackBar(messages.join('\n'), 'error', messages.length > 1);
                        }
                        return;
                    }
                    if (error.status === 400 && responseBody?.error === Error.BAD_REQUEST) {
                        if (!!responseBody.errorDescription) {
                            this.snackbarService.showSnackBar(responseBody.errorDescription, 'error', false);
                        } else {
                            this.snackbarService.showSnackBar('i18n_af_snackBarMessageBadRequest', 'error', false);
                        }
                        return;
                    } else if (error.status === 403) {
                        this.snackbarService.showSnackBar('i18n_af_snackBarMessage#AF_ACCESS_DENIED', 'error', false);
                        return;
                    } else if (error.status === 404) {
                        errorType = Error.PAGE_NOT_FOUND;
                    } else if (responseBody?.requestId) {
                        queryParams[ERROR_REQUEST_ID_PARAM] = responseBody.requestId;
                    }
                }
                queryParams[ERROR_PARAM] = errorType;
                this.routerService.navigate(ERROR_PATH, {queryParams});
            });
        });
    }

}

export async function prepareHttpErrorForBusinessParsing(errorResponse: HttpErrorResponse): Promise<HttpErrorResponse> {
    const isJsonBlob = errorResponse.error instanceof Blob && errorResponse.error.type.startsWith('application/json');
    if (!isJsonBlob) {
        return Promise.resolve(errorResponse);
    }
    try {
        const text = await errorResponse.error.text();
        const parsed = JSON.parse(text);
        return ({...errorResponse, error: parsed});
    } catch {
        return errorResponse;
    }
}

export enum Error {
    PAGE_NOT_FOUND = 'PAGE_NOT_FOUND',
    SERVER_ERROR = 'SERVER_ERROR',
    BAD_REQUEST = 'BAD_REQUEST',
    NETWORK_CONNECTIVITY_ERROR = 'NETWORK_CONNECTIVITY_ERROR'
}

interface ValidationErrorItem {
    message: string;
}
